import { useCallback, useMemo, useEffect } from "react";
import * as R from "ramda";
import { useHistory, useRouteMatch, useLocation } from "react-router-dom";
import { debounce } from "throttle-debounce";

import useStorage from "../../../../../hooks/storage/useStorage";
import { createQueryString } from "../../../../../utils/queryStrings";
import {
  getFiltersForList,
  getStrictFiltersForOpportunity,
  getServerFilters,
  filterCandidates,
  getFiltersSessionStorageKey
} from "../../../../../utils/candidatesFilters";
import { usePrevious } from "../../../../../hooks/custom/usePrevious";

const saveFiltersToQueryString = debounce(
  250,
  ({ filters, history, routeMatch }) => {
    history.replace(`${routeMatch.url}?${createQueryString(filters)}`);
  }
);

export const DEFAULT_SERVER_FILTERS_FOR_OPPORTUNITY = [
  "searchText",
  "agerange",
  "country",
  "province",
  "district",
  "education",
  "expectedsalary",
  "languages",
  "referral",
  "registered",
  "workexperience",
  "fieldsofstudy",
  "professions",
  "skills",
  "page",
  "skills",
  "chatbotTags",
  "tags",
  "strictFilters",
  "tagprofile",
  "score",
  "sort",
  "applydate",
  "previousmatch",
  "screeningstatus"
];

export const DEFAULT_SERVER_FILTERS_FOR_CANDIDATES_BASE = [
  "searchText",
  "agerange",
  "country",
  "province",
  "district",
  "education",
  "expectedsalary",
  "languages",
  "referral",
  "registered",
  "workexperience",
  "fieldsofstudy",
  "professions",
  "skills",
  "page"
];

const useFilters = ({
  candidates,
  list,
  filterKey,
  filtersUsed,
  opportunity,
  onServerFiltersChange,
  resetMultipleSelectCandidates,
  serverFiltersList
}) => {
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const location = useLocation();

  const strictFilterOptions = useMemo(
    () => opportunity && getStrictFiltersForOpportunity(opportunity),
    [opportunity]
  );

  const [filters, setFilters] = useStorage(
    sessionStorage,
    getFiltersSessionStorageKey(filterKey),
    () =>
      getFiltersForList({
        opportunity,
        filterKey,
        filtersUsed,
        searchQuery: location.search,
        onlyServerFilters: false
      }),
    true
  );

  const country = filters?.country || [];
  const province = filters?.province || [];

  const setFilter = useCallback(
    (key, value, overridePage = false) => {
      setFilters(newFilters => {
        const isServerFilter = serverFiltersList.includes(key);
        const isExcludingFilter = key === "all" || key === "none";
        const sendRequest = isServerFilter || isExcludingFilter;

        if (value && value.target) value = value.target.value;

        if (key === "all") {
          newFilters = {
            ...newFilters,
            page: 1,
            fieldsofstudy: R.propOr([], "fieldsOfStudy", opportunity),
            education: R.propOr([], "educationLevelsAdvanced", opportunity).map(
              e => e.level
            ),
            agerange:
              R.length(R.propOr([], "ageRange", opportunity)) !== 0
                ? opportunity.ageRange
                : [16, 65],
            strictFilters:
              R.length(R.propOr([], "languages", opportunity)) !== 0
                ? ["languages"]
                : [],
            sort: "score"
          };
        } else if (key === "none") {
          newFilters = {
            ...newFilters,
            page: 1,
            fieldsofstudy: [],
            education: [],
            agerange: [16, 65],
            strictFilters: [],
            sort: "score"
          };
        } else {
          newFilters =
            overridePage && isServerFilter
              ? R.mergeRight(newFilters, {
                  [key]: value,
                  page: 1
                })
              : R.assoc(key, value, newFilters);
        }

        // Clear district and province filters if needed
        if (newFilters.country?.length < country.length) {
          // console.log("province reset");
          newFilters.province = [];
        }
        if (newFilters.province?.length < province.length) {
          // console.log("district reset");
          newFilters.district = [];
        }

        // TODO revise, this only sets filters in QS after they're set
        saveFiltersToQueryString({
          filters: newFilters,
          history,
          routeMatch
        });

        // If filter is back-end based (needs a refetch of the list) execute onServerFiltersChange
        if (sendRequest && onServerFiltersChange) {
          resetMultipleSelectCandidates();
          onServerFiltersChange({
            filters: getServerFilters(newFilters)
          });
        }

        return newFilters;
      });
    },
    [
      country.length,
      province.length,
      history,
      onServerFiltersChange,
      opportunity,
      resetMultipleSelectCandidates,
      routeMatch,
      serverFiltersList,
      setFilters
    ]
  );

  const handleFiltersChange = useCallback(
    key => value => {
      if (key === "all" || key === "none") setFilter(key, opportunity);
      else if (filtersUsed.page && key !== "page") setFilter(key, value, true);
      else setFilter(key, value);
    },
    [filtersUsed.page, opportunity, setFilter]
  );

  // Filter candidates based on front-end filters
  const filteredCandidates = useMemo(
    // TODO revert this if necessary
    () =>
      filterCandidates(candidates, filters, serverFiltersList, list, opportunity),
    [candidates, filters, serverFiltersList, list, opportunity]
  );

  const isEmpty = R.isEmpty(filteredCandidates);

  const areFiltersApplied =
    filters.searchText !== "" ||
    (filters.strictFilters && !!filters.strictFilters.length) ||
    filters.referral;

  const previousFilters = usePrevious(filters);

  useEffect(() => {
    if (R.not(R.equals(filters, previousFilters))) {
      window.scroll({
        top: 0,
        behavior: "smooth"
      });
    }
  }, [filters, previousFilters]);

  return {
    filteredCandidates,
    filters,
    setFilter,
    handleFiltersChange,
    isEmpty,
    areFiltersApplied,
    strictFilterOptions
  };
};

export default useFilters;
